import { useState, useCallback, useMemo, useEffect } from 'react';
import { CACHE_TIME } from '@/config/queryDefaults';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  SelectChangeEvent,
  Chip,
  Tooltip
} from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { entityMetadataService, EntityMetadata, EntityFieldMetadata } from '@/services/core/EntityMetadataService';
import { useTranslation } from 'react-i18next';
import { apiClient, getErrorMessage } from '@/lib/apiClient';
import { ViewToolbar } from '@/components/ViewToolbar';
import { saveShortcutLabel } from '@/hooks/useSaveShortcut';
import { ValidationErrorBanner } from '@/components/forms/ValidationErrorBanner';
import { ValidationErrorPanel } from '@/components/forms/ValidationErrorPanel';
import { ValidationError, getValidationErrors } from '@/lib/validationErrors';
import { checkOfflineAndNotify } from '@/lib/offlineGuard';
import { GRID_ROW_HEIGHT } from '@/lib/gridStyles';
import { usePersistedFilters } from '@/hooks/usePersistedFilters';

// --- Types ---

interface AbpRole {
  id: string;
  name: string;
}

interface FieldRestrictionEntry {
  fieldName: string;
  restrictionType: string; // "HIDDEN" | "READ_ONLY" (JSON string enum from backend)
}

interface FieldRestrictionSetWithEntries {
  id: string;
  moduleName: string;
  entityName: string;
  roleId: string;
  roleName: string;
  entries: FieldRestrictionEntry[];
}

interface FieldRow {
  id: string;
  fieldName: string;
  type: string;
  label: string;
  required: boolean;
  restriction: 'NONE' | 'READ_ONLY' | 'HIDDEN';
}

// Backend FieldRestrictionType enum values (serialized as strings by JsonStringEnumConverter)
function restrictionTypeToString(type: string): 'READ_ONLY' | 'HIDDEN' {
  return type === 'HIDDEN' ? 'HIDDEN' : 'READ_ONLY';
}

function restrictionStringToType(str: string): string {
  return str === 'HIDDEN' ? 'HIDDEN' : 'READ_ONLY';
}

// --- API hooks ---

function useAllEntities() {
  return useQuery({
    queryKey: ['entityMetadata', 'all'],
    queryFn: () => entityMetadataService.getAllEntities(),
    staleTime: CACHE_TIME.METADATA,
  });
}

function useEntityFields(entityName: string) {
  return useQuery({
    queryKey: ['entityMetadata', 'fields', entityName],
    queryFn: () => entityMetadataService.getEntityFields(entityName),
    enabled: !!entityName,
    staleTime: CACHE_TIME.METADATA,
  });
}

function useRoles() {
  return useQuery({
    queryKey: ['identity', 'roles'],
    queryFn: async () => {
      const response = await apiClient.get<{ items: AbpRole[] }>('/api/identity/roles/all');
      // ABP returns { items: [...] } for the /all endpoint
      const data = response.data;
      return Array.isArray(data) ? data : (data as any).items || [];
    },
    staleTime: CACHE_TIME.METADATA,
  });
}

function useExistingRestrictions(moduleName: string, entityName: string, roleId: string) {
  return useQuery({
    queryKey: ['fieldRestrictions', moduleName, entityName, roleId],
    queryFn: async () => {
      const response = await apiClient.get<FieldRestrictionSetWithEntries>(
        `/api/core/fieldrestrictionset/by-entity-and-role`,
        { params: { moduleName, entityName, roleId } }
      );
      return response.data;
    },
    enabled: !!moduleName && !!entityName && !!roleId,
  });
}

// Stable empty arrays to avoid new references on every render
const EMPTY_ENTITIES: EntityMetadata[] = [];
const EMPTY_FIELDS: EntityFieldMetadata[] = [];
const EMPTY_ROLES: AbpRole[] = [];

// --- Component ---

export const FieldRestrictionsManagementView = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Selection state (persisted to localStorage)
  const [filterState, setFilterState] = usePersistedFilters('fieldRestrictions', {
    selectedModule: '',
    selectedEntity: '',
    selectedRoleId: '',
  });
  const { selectedModule, selectedEntity, selectedRoleId } = filterState;

  // UI state
  const [fieldRows, setFieldRows] = useState<FieldRow[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);
  const [validationPanelOpen, setValidationPanelOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Data queries - use stable empty arrays as defaults
  const { data: allEntities = EMPTY_ENTITIES, isLoading: isLoadingEntities } = useAllEntities();
  const { data: entityFields = EMPTY_FIELDS, isLoading: isLoadingFields } = useEntityFields(selectedEntity);
  const { data: roles = EMPTY_ROLES, isLoading: isLoadingRoles } = useRoles();
  const { data: existingRestrictions, isLoading: isLoadingRestrictions } = useExistingRestrictions(
    selectedModule, selectedEntity, selectedRoleId
  );

  // Derive modules from entities
  const modules = useMemo(() => {
    const moduleSet = new Set(allEntities.map(e => e.moduleName));
    return Array.from(moduleSet).sort();
  }, [allEntities]);

  // Filter entities by selected module
  const filteredEntities = useMemo(() => {
    if (!selectedModule) return [];
    return allEntities
      .filter(e => e.moduleName === selectedModule)
      .sort((a, b) => a.entityName.localeCompare(b.entityName));
  }, [allEntities, selectedModule]);

  // Build field rows when fields + restrictions are loaded
  const buildFieldRows = useCallback(() => {
    if (!entityFields.length) {
      setFieldRows([]);
      return;
    }

    const restrictionMap = new Map<string, string>();
    if (existingRestrictions?.entries) {
      for (const entry of existingRestrictions.entries) {
        restrictionMap.set(entry.fieldName, entry.restrictionType);
      }
    }

    const rows: FieldRow[] = entityFields.map(field => {
      const restrictionType = restrictionMap.get(field.name);
      let restriction: 'NONE' | 'READ_ONLY' | 'HIDDEN' = 'NONE';
      if (restrictionType !== undefined) {
        restriction = restrictionTypeToString(restrictionType);
      }

      return {
        id: field.name,
        fieldName: field.name,
        type: field.type,
        label: field.label || field.name,
        required: field.required,
        restriction,
      };
    });

    setFieldRows(rows);
    setHasChanges(false);
  }, [entityFields, existingRestrictions]);

  // Rebuild rows when fields or restrictions change
  useEffect(() => {
    buildFieldRows();
  }, [buildFieldRows]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const entries = fieldRows
        .filter(r => r.restriction !== 'NONE')
        .map(r => ({
          fieldName: r.fieldName,
          restrictionType: restrictionStringToType(r.restriction),
        }));

      const response = await apiClient.post<FieldRestrictionSetWithEntries>(
        '/api/core/fieldrestrictionset/save-restrictions',
        {
          moduleName: selectedModule,
          entityName: selectedEntity,
          roleId: selectedRoleId,
          entries,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      setHasChanges(false);
      setSaveSuccess(true);
      setApiValidationErrors([]);
      setTimeout(() => setSaveSuccess(false), 3000);
      queryClient.invalidateQueries({ queryKey: ['fieldRestrictions'] });
    },
    onError: (error: unknown) => {
      const validationErrors = getValidationErrors(error);
      if (validationErrors.length > 0) {
        setApiValidationErrors(validationErrors);
        setValidationPanelOpen(true);
      } else {
        const message = getErrorMessage(error);
        setApiValidationErrors([{
          field: 'form',
          message,
          instancePath: '#/properties/form',
          members: ['form']
        }]);
        setValidationPanelOpen(true);
      }
    }
  });

  // Handle restriction change in grid
  const handleRestrictionChange = useCallback((fieldName: string, newValue: string) => {
    setFieldRows(prev => prev.map(row =>
      row.fieldName === fieldName
        ? { ...row, restriction: newValue as 'NONE' | 'READ_ONLY' | 'HIDDEN' }
        : row
    ));
    setHasChanges(true);
    setSaveSuccess(false);
  }, []);

  // Module change handler
  const handleModuleChange = (event: SelectChangeEvent) => {
    setFilterState({ selectedModule: event.target.value, selectedEntity: '', selectedRoleId: '' });
    setFieldRows([]);
    setHasChanges(false);
  };

  // Entity change handler
  const handleEntityChange = (event: SelectChangeEvent) => {
    setFilterState(prev => ({ ...prev, selectedEntity: event.target.value }));
    setFieldRows([]);
    setHasChanges(false);
  };

  // Role change handler
  const handleRoleChange = (event: SelectChangeEvent) => {
    setFilterState(prev => ({ ...prev, selectedRoleId: event.target.value }));
    setHasChanges(false);
  };

  // Grid columns
  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'fieldName',
      headerName: t('fieldRestrictions.colField'),
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'type',
      headerName: t('common.type'),
      width: 100,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: 'required',
      headerName: t('fieldRestrictions.colRequired'),
      width: 80,
      type: 'boolean',
    },
    {
      field: 'restriction',
      headerName: t('fieldRestrictions.colRestriction'),
      width: 160,
      renderCell: (params) => (
        <Select
          value={params.value}
          onChange={(e) => handleRestrictionChange(params.row.fieldName, e.target.value)}
          size="small"
          variant="standard"
          fullWidth
          sx={{ fontSize: '0.875rem' }}
        >
          <MenuItem value="NONE">
            <Typography variant="body2" color="text.secondary">{t('fieldRestrictions.restrictionNone')}</Typography>
          </MenuItem>
          <MenuItem value="READ_ONLY">
            <Chip label={t('fieldRestrictions.restrictionReadOnly')} size="small" color="warning" sx={{ cursor: 'pointer' }} />
          </MenuItem>
          <MenuItem value="HIDDEN">
            <Chip label={t('fieldRestrictions.restrictionHidden')} size="small" color="error" sx={{ cursor: 'pointer' }} />
          </MenuItem>
        </Select>
      ),
    },
    {
      field: 'label',
      headerName: t('fieldRestrictions.colDescription'),
      flex: 2,
      minWidth: 200,
    },
  ], [handleRestrictionChange, t]);

  const isSelectionComplete = selectedModule && selectedEntity && selectedRoleId;
  const isLoading = isLoadingEntities || isLoadingFields || isLoadingRoles || isLoadingRestrictions;
  const restrictedCount = fieldRows.filter(r => r.restriction !== 'NONE').length;

  const handleSave = () => {
    if (checkOfflineAndNotify(setApiValidationErrors, setValidationPanelOpen, t)) return;
    saveMutation.mutate();
  };
  const saveDisabled = !hasChanges || !isSelectionComplete || saveMutation.isPending;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Validation Error Banner */}
      <ValidationErrorBanner
        errors={apiValidationErrors}
        onExpand={() => setValidationPanelOpen(true)}
        onClear={() => {
          setApiValidationErrors([]);
          setValidationPanelOpen(false);
        }}
      />

      <ViewToolbar
        onRefresh={isSelectionComplete ? buildFieldRows : undefined}
        hasUnsavedChanges={hasChanges}
        onSave={handleSave}
        saveDisabled={saveDisabled}
      >
        <Tooltip title={saveShortcutLabel()}>
          <span>
            <Button
              variant="outlined"
              disabled={saveDisabled}
              onClick={handleSave}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                ...(saveDisabled
                  ? { borderColor: 'grey.300', color: 'grey.400' }
                  : { borderColor: 'primary.main', color: 'primary.main' }),
              }}
            >
              {saveMutation.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </span>
        </Tooltip>
      </ViewToolbar>

      {/* Main Content Area with Drawer */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Main Content */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 500 }}>
            {t('fieldRestrictions.title')}
          </Typography>

          {saveSuccess && (
            <Alert severity="success" sx={{ mt: 1, mb: 1 }}>
              {t('fieldRestrictions.saved')}
            </Alert>
          )}

          {/* Selection controls */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>{t('fieldRestrictions.module')}</InputLabel>
              <Select
                value={selectedModule}
                onChange={handleModuleChange}
                label={t('fieldRestrictions.module')}
              >
                {modules.map(m => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }} disabled={!selectedModule}>
              <InputLabel>{t('fieldRestrictions.entity')}</InputLabel>
              <Select
                value={selectedEntity}
                onChange={handleEntityChange}
                label={t('fieldRestrictions.entity')}
              >
                {filteredEntities.map(e => (
                  <MenuItem key={e.entityName} value={e.entityName}>{e.entityName}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t('fieldRestrictions.role')}</InputLabel>
              <Select
                value={selectedRoleId}
                onChange={handleRoleChange}
                label={t('fieldRestrictions.role')}
              >
                {roles.map((r: AbpRole) => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {isSelectionComplete && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
                {t('fieldRestrictions.restrictedCount', { restricted: restrictedCount, total: fieldRows.length })}
              </Typography>
            )}
          </Box>

          {/* Instructions or grid */}
          {!isSelectionComplete ? (
            <Alert severity="info">{t('fieldRestrictions.selectAll')}</Alert>
          ) : (
            <Box sx={{ flex: 1 }}>
              <DataGridPremium
                rows={fieldRows}
                columns={columns}
                loading={isLoading}
                density="compact"
                rowHeight={GRID_ROW_HEIGHT}
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                pageSizeOptions={[50, 100]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 50 } }
                }}
                sx={{
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                  }
                }}
              />
            </Box>
          )}
        </Box>

        {/* Validation Error Panel - right side slide-out */}
        <ValidationErrorPanel
          open={validationPanelOpen}
          onClose={() => setValidationPanelOpen(false)}
          errors={apiValidationErrors}
        />
      </Box>
    </Box>
  );
};
